/**
 * Payments (INV-02 "Payments"): every detected transfer against this invoice,
 * with its state, its transaction identity and its applied/excess split.
 *
 * Two honesty rules are enforced here rather than documented and hoped for:
 *
 *   - **Amounts are never parsed into JavaScript numbers.** Every amount is a
 *     base-unit string next to `{symbol, decimals}` and is rendered through the
 *     BigInt formatter in `features/invoicing/money.ts`. There is no `Number(...)`
 *     in this file and no arithmetic at all: the service is the ledger.
 *   - **No confirmation count is invented.** The design intent asks for
 *     confirmations against a per-network finality threshold, and this contract's
 *     `Payment` carries no such count — it carries a state and the timestamps at
 *     which the payment was detected, became final and was applied. The table
 *     shows those and says so in `paymentFinalityNote`; a fabricated "12/12"
 *     would be the `fabricated_balance_or_count` bug signal this page's spec
 *     lists.
 */

import { EmptyState } from "@/components/ui/feedback";
import { Table, TableWrapper } from "@/components/ui/table";
import { formatMoney } from "@/features/invoicing/money";
import { paymentStatePresentation } from "@/features/invoicing/status";
import type { Payment, UnappliedReason } from "@/features/invoicing/types";
import { DETAIL_COPY } from "./copy";
import { StatusChip } from "./status-axes";

/** Labels for the two `Payment.unappliedReason` values; no other value is named. */
const UNAPPLIED_REASON_LABEL: Record<UnappliedReason, string> = {
  WRONG_ASSET: "Wrong asset",
  INVOICE_NOT_PAYABLE: "Invoice not payable",
};

function unappliedReasonLabel(reason: UnappliedReason | null | undefined): string | null {
  if (reason === null || reason === undefined) return null;
  return Object.prototype.hasOwnProperty.call(UNAPPLIED_REASON_LABEL, reason)
    ? UNAPPLIED_REASON_LABEL[reason]
    : null;
}

/** A cell value that may be absent renders an explicit dash, never a blank. */
function Cell({ value, mono = false }: { value: string | null | undefined; mono?: boolean }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted">{DETAIL_COPY.emptyValue}</span>;
  }
  return <span className={mono ? "break-all font-mono text-[12px] text-muted" : undefined}>{value}</span>;
}

function MoneyCell({ money }: { money: Payment["amount"] | null | undefined }) {
  if (!money) return <Cell value={null} />;
  return (
    <span className="whitespace-nowrap font-semibold text-ink">
      {formatMoney(money)} <span className="font-normal text-muted">{money.asset.symbol}</span>
    </span>
  );
}

export function PaymentsTable({ payments }: { payments: Payment[] }) {
  return (
    <section aria-labelledby="invoice-payments-heading" data-testid="invoice-payments">
      <h2 id="invoice-payments-heading" className="mb-2 text-sm font-bold text-ink">
        {DETAIL_COPY.paymentsHeading}
      </h2>
      {payments.length === 0 ? (
        <EmptyState
          card
          title={DETAIL_COPY.paymentsEmptyTitle}
          description={DETAIL_COPY.paymentsEmptyDescription}
        />
      ) : (
        <PaymentsBody payments={payments} />
      )}
    </section>
  );
}

function PaymentsBody({ payments }: { payments: Payment[] }) {
  return (
    <div>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th scope="col">{DETAIL_COPY.paymentNetwork}</th>
              <th scope="col">{DETAIL_COPY.paymentAmount}</th>
              <th scope="col">{DETAIL_COPY.paymentTxHash}</th>
              <th scope="col">{DETAIL_COPY.paymentState}</th>
              <th scope="col">{DETAIL_COPY.paymentDetectedAt}</th>
              <th scope="col">{DETAIL_COPY.paymentFinalAt}</th>
              <th scope="col">{DETAIL_COPY.paymentAppliedAmount}</th>
              <th scope="col">{DETAIL_COPY.paymentExcessAmount}</th>
              <th scope="col">{DETAIL_COPY.paymentCaseRef}</th>
              <th scope="col">{DETAIL_COPY.paymentJournalEntry}</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.paymentId} data-testid={`invoice-payment-${p.paymentId}`}>
                <td>
                  <div className="font-semibold text-ink">{p.network}</div>
                  <div className="font-mono text-[11px] text-muted">{p.paymentId}</div>
                </td>
                <td>
                  <MoneyCell money={p.amount} />
                </td>
                <td className="max-w-[220px]">
                  <div className="break-all font-mono text-[12px] text-ink">{p.txHash}</div>
                  <div className="font-mono text-[11px] text-muted">
                    #{p.logIndex}
                    {p.sourceAddress ? ` · ${p.sourceAddress}` : ""}
                  </div>
                </td>
                <td>
                  <StatusChip
                    presentation={paymentStatePresentation(p.state)}
                    raw={String(p.state)}
                    testId={`invoice-payment-state-${p.paymentId}`}
                  />
                  {unappliedReasonLabel(p.unappliedReason) && (
                    <div className="mt-1 text-[11px] text-warning">
                      {unappliedReasonLabel(p.unappliedReason)}
                    </div>
                  )}
                </td>
                <td>
                  <Cell value={p.detectedAt} mono />
                </td>
                <td>
                  <Cell value={p.finalAt} mono />
                </td>
                <td>
                  {p.appliedAmount ? (
                    <MoneyCell money={p.appliedAmount} />
                  ) : (
                    <Cell value={p.appliedAt} mono />
                  )}
                </td>
                <td>
                  <MoneyCell money={p.excessAmount} />
                </td>
                <td>
                  <Cell value={p.caseRef} />
                </td>
                <td>
                  <Cell value={p.journalEntryId} mono />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      <p className="mt-2 max-w-[680px] text-xs leading-relaxed text-muted">
        {DETAIL_COPY.paymentFinalityNote}
      </p>
    </div>
  );
}
